import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, SlidersHorizontal, Plus, ArrowUp, CheckCircle2 } from 'lucide-react';
import { RecipeCard } from './RecipeCard';
import { RecipeDetail } from './RecipeDetail';
import { FilterModal } from './FilterModal';
import { ImportRecipeModal } from './ImportRecipeModal';
import { ManualRecipeForm } from './ManualRecipeForm';
import { ConfirmationDrawer } from './ConfirmationDrawer';
import BottomNav from './BottomNav';
import { apiGet } from '../utils/apiClient';
import { useAuth } from '../context/AuthContext';
import { useRecipeActions } from '../hooks/useRecipeActions';
import { effectiveTotalMinutes, toMinutes } from '../utils/recipeTime';

// Special calculated filters: matched on prepTime + cookTime rather than recipe tags.
// maxMinutes is inclusive — a 30-minute recipe belongs in "Up to 30 mins".
const TIMING_FILTERS = [
  { label: 'Up to 15 mins', maxMinutes: 15 },
  { label: 'Up to 30 mins', maxMinutes: 30 },
];
// Earlier wording, kept only so a recipe carrying one of these as a literal tag stays out
// of the Other section rather than reappearing as a lookalike pill.
const LEGACY_TIMING_LABELS = ['Under 15 minutes', 'Under 30 minutes'];
const TIMING_FILTER_LABELS = TIMING_FILTERS.map(f => f.label);
const EXCLUDED_TAG_LABELS = [...TIMING_FILTER_LABELS, ...LEGACY_TIMING_LABELS];

// Format numeric quantities: whole numbers stay as-is, others rounded to 2 decimals
const formatQuantityNumber = (q) => {
  if (typeof q !== 'number' || !isFinite(q)) return String(q);
  const roundedInt = Math.round(q);
  if (Math.abs(q - roundedInt) < 1e-9) return String(roundedInt);
  const rounded2 = Math.round(q * 100) / 100;
  return String(rounded2);
};

export function Recipes({ activeTab, onTabChange, onEditRecipe, onDeleteRecipe, onAddToMealPlan, refreshKey, userId = 'user-default', successBannerMessage = '' }) {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const handleTabChange = (tab) => {
    if (typeof onTabChange === 'function') return onTabChange(tab);
    switch (tab) {
      case 'meal-plan':
        navigate('/MealPlan');
        break;
      case 'shopping-list':
        navigate('/ShoppingList');
        break;
      case 'recipes':
        navigate('/AllRecipes');
        break;
      case 'more':
        navigate('/More');
        break;
      default:
        break;
    }
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [selectedMealTypes, setSelectedMealTypes] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const listScrollYRef = useRef(0);
  const hasVisitedDetailRef = useRef(false);
  const successTimerRef = useRef(null);
  // Only the household owner (the subscription holder) can edit or delete recipes, so the
  // actions menu is hidden for members. Same ownerUid comparison More.jsx uses for its
  // member-only rows. Defaults to false until the household loads so the menu can't flash
  // for a member; a solo user is the owner of their own household, so they still see it.
  const [isHouseholdOwner, setIsHouseholdOwner] = useState(false);

  // Normalize backend recipe shape into the UI-friendly format
  const normalizeRecipe = (raw) => {
    const image =
      typeof raw.image === 'string' ? raw.image :
      (raw.image?.src || raw.image?.url || (Array.isArray(raw.imageUrls) ? raw.imageUrls[0] : '') || raw.image_url || '');
    const imageThumb =
      (typeof raw.imageThumb === 'string' && raw.imageThumb) ||
      (typeof raw.thumbnail === 'string' && raw.thumbnail) ||
      (typeof raw.thumbUrl === 'string' && raw.thumbUrl) ||
      (typeof raw.recipe?.imageThumb === 'string' && raw.recipe.imageThumb) ||
      '';

    const ingredients = Array.isArray(raw.ingredients) ? raw.ingredients.map((ing) => {
      const name = ing.name || ing.ingredient || '';
      const unit = ing.unit || ing.units || '';
      const q = ing.quantity;
      let quantityStr = '';
      if (typeof q === 'number') {
        const formatted = formatQuantityNumber(q);
        quantityStr = unit ? `${formatted} ${unit}` : formatted;
      } else if (typeof q === 'string') {
        quantityStr = unit ? `${q} ${unit}` : q;
      } else {
        quantityStr = unit ? `${unit}` : '';
      }
      return {
        name,
        quantity: quantityStr.trim(),
        category: ing.category || undefined,
      };
    }) : [];

    // Helper: normalize possible method/instructions shapes to array of strings
    const toLines = (val) => {
      if (!val) return [];
      if (Array.isArray(val)) {
        return val
          .map(x => (typeof x === 'string' ? x : (x && typeof x === 'object' ? (x.text || '') : '')))
          .map(s => s.trim())
          .filter(Boolean);
      }
      if (typeof val === 'string') {
        const trimmed = val.trim();
        const lines = trimmed.includes('\n') ? trimmed.split(/\r?\n/) : trimmed.split(/(?<=\\.)\\s+/);
        return lines.map(s => s.trim()).filter(Boolean);
      }
      return [];
    };
    // Prefer first non-empty candidate among common fields
    const methodCandidates = [raw.method, raw.instructions, raw.recipeInstructions, raw.directions, raw.steps];
    let method = [];
    for (const c of methodCandidates) {
      const lines = toLines(c);
      if (lines.length) { method = lines; break; }
    }

    const tags = Array.isArray(raw.tags) ? raw.tags : [];
    const source = raw.source || raw.sourceUrl || '';
    const imagePath = raw.imagePath || '';
    const imageThumbPath = raw.imageThumbPath || raw.thumbnailPath || raw.thumbPath || '';
    const shared = raw.shared === true || raw.shared === 'true' || raw.shared === 1 || raw.shared === '1';

    return {
      id: raw._id || raw.id,
      name: raw.name || raw.title || '',
      image,
      imagePath,
      imageThumb,
      imageThumbPath,
      prepTime: toMinutes(raw.prepTime),
      cookTime: toMinutes(raw.cookTime),
      totalTime: toMinutes(raw.totalTime),
      mealType: raw.mealType || '',
      tags,
      servings: Number(raw.servings) || 1,
      unitSystem: raw.unitSystem || 'metric',
      shared,
      ingredients,
      method,
      source,
      nutrition: raw.nutrition || { calories: 0, protein: 0, carbs: 0, fat: 0 },
    };
  };

    // Responsive screen detection
  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 768px)');
    
    const handleMediaChange = (e) => {
      setIsLargeScreen(e.matches);
    };
    
    // Set initial value
    setIsLargeScreen(mediaQuery.matches);
    
    // Listen for changes
    mediaQuery.addEventListener('change', handleMediaChange);
    
    // Cleanup
    return () => mediaQuery.removeEventListener('change', handleMediaChange);
  }, []);

  // Resolve household ownership to decide whether the recipe actions menu renders.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiGet('/households/me');
        if (!cancelled) {
          setIsHouseholdOwner(!!(data?.ownerUid && authUser?.uid && data.ownerUid === authUser.uid));
        }
      } catch (err) {
        // 404 means no household exists yet, which mirrors the backend treating a
        // household-less user as their own owner. Any other failure fails closed — the
        // backend is the real guard, so hiding the menu only costs the owner a retry.
        if (!cancelled) setIsHouseholdOwner(err?.status === 404);
        if (err?.status !== 404) {
          console.warn('[Recipes] GET /api/households/me failed:', err?.message || err);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [authUser?.uid]);

  // Fetch recipes from backend API (reusable)
  const fetchRecipes = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await apiGet('/recipes');
      const list = Array.isArray(data) ? data : [];
      const normalized = list.map(normalizeRecipe);
      setRecipes(normalized);
      return normalized;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      console.warn('[Recipes] GET /api/recipes failed:', msg);
      setError(msg);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!mounted) return;
      const normalized = await fetchRecipes();
      if (!mounted) return;
      // If a recipe is currently selected, keep the details open
      // but refresh it from the latest list. If it no longer exists
      // (e.g. after delete), close the detail view.
      if (selectedRecipe) {
        const updated = normalized.find(r => r.id === selectedRecipe.id);
        setSelectedRecipe(updated || null);
      }
    })();
    return () => { mounted = false; };
  // We only refresh when the parent bump refreshKey; selectedRecipe
  // updates are handled inside this effect.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  // Get all unique tags from the active list (API or fallback), excluding the special time filter labels
  const allTagsRaw = Array.from(
    new Set(
      recipes.flatMap(r => Array.isArray(r.tags) ? r.tags : [])
    )
  );
  const allTags = allTagsRaw.filter(tag => !EXCLUDED_TAG_LABELS.includes(tag));

  // Timing-section filters: the special time-based pills
  const timingFilters = TIMING_FILTER_LABELS;

  // Other-section filters: every remaining recipe tag
  const otherFilters = allTags;

  // Filter recipes based on search and filters
  const filteredRecipes = recipes.filter(recipe => {
    const matchesSearch = recipe.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (Array.isArray(recipe.ingredients) && recipe.ingredients.some(ing => ing.name?.toLowerCase().includes(searchQuery.toLowerCase())));
    const matchesMealType = selectedMealTypes.length === 0 || selectedMealTypes.includes(recipe.mealType);

    // Split selected tags into special time filters and normal tags
    const selectedTimingFilters = TIMING_FILTERS.filter(f => selectedTags.includes(f.label));
    const otherSelectedTags = selectedTags.filter(tag => !EXCLUDED_TAG_LABELS.includes(tag));

    const matchesTags = otherSelectedTags.length === 0 || otherSelectedTags.some(tag => Array.isArray(recipe.tags) && recipe.tags.includes(tag));

    // Timing filters: use prepTime + cookTime in minutes; recipes with neither time
    // set are excluded. Multiple timing pills are OR'd, matching the other groups.
    const total = effectiveTotalMinutes(recipe);
    const matchesTiming = selectedTimingFilters.length === 0 ||
      (total !== null && selectedTimingFilters.some(f => total <= f.maxMinutes));

    return matchesSearch && matchesMealType && matchesTags && matchesTiming;
  });

  const activeFilterCount = selectedMealTypes.length + selectedTags.length;

  const handleApplyFilters = (mealTypes, tags) => {
    setSelectedMealTypes(mealTypes);
    setSelectedTags(tags);
    setIsFilterOpen(false);
  };

  const handleAddRecipe = () => {
    setIsImportModalOpen(true);
  };

  const handleRecipeSaved = async () => {
    await fetchRecipes();
    setSuccessMessage('Recipe added successfully.');

    if (successTimerRef.current) {
      window.clearTimeout(successTimerRef.current);
    }
    successTimerRef.current = window.setTimeout(() => {
      setSuccessMessage('');
      successTimerRef.current = null;
    }, 7000);
  };

  // Restore scroll position when returning from a recipe detail view
  useEffect(() => {
    if (!selectedRecipe && hasVisitedDetailRef.current) {
      window.scrollTo(0, listScrollYRef.current || 0);
    } else if (selectedRecipe) {
      // Ensure detail view opens scrolled to top
      window.scrollTo(0, 0);
    }
  }, [selectedRecipe]);

  useEffect(() => {
    return () => {
      if (successTimerRef.current) {
        window.clearTimeout(successTimerRef.current);
      }
    };
  }, []);

  // Self-contained recipe actions for when this page is a standalone route (no MealPlan
  // props). When rendered as MealPlan's internal tab, the parent supplies the handlers and
  // owns the drawers, so these stay dormant (props-first, like the ShoppingList week nav).
  const showRecipeBanner = (msg) => {
    setSuccessMessage(msg);
    if (successTimerRef.current) window.clearTimeout(successTimerRef.current);
    successTimerRef.current = window.setTimeout(() => setSuccessMessage(''), 5000);
  };
  const recipeActions = useRecipeActions({
    showSuccessBanner: showRecipeBanner,
    setRecipesRefreshKey: () => { fetchRecipes(); },
    setViewingMealRecipe: setSelectedRecipe,
  });
  const usesSelfActions = typeof onEditRecipe !== 'function';
  const editHandler = onEditRecipe || recipeActions.handleEditRecipe;
  const deleteHandler = onDeleteRecipe || recipeActions.handleDeleteRecipe;
  // Add-to-plan needs the meal plan's week context; standalone we hand off to MealPlan,
  // which opens its existing add-to-plan drawer (real weeksData + conflict detection).
  const addHandler = onAddToMealPlan || ((recipe) => navigate('/MealPlan', { state: { addRecipe: recipe } }));

  if (selectedRecipe) {
    return (
      <RecipeDetail
        recipe={selectedRecipe}
        onBack={() => setSelectedRecipe(null)}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        onEdit={isHouseholdOwner ? editHandler : undefined}
        onDelete={isHouseholdOwner ? deleteHandler : undefined}
        onAddToMealPlan={addHandler}
        showAddToMealPlan={true}
        showEditDelete={isHouseholdOwner}
        successBannerMessage={successBannerMessage}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background flex justify-center">
      <div 
        className="w-full bg-background min-h-screen relative pb-24"
        style={{ 
          maxWidth: isLargeScreen ? '1200px' : '448px',
          transition: 'max-width 0.3s ease'
        }}
      >
        {/* Status Bar Space */}
        <div className="h-8" />
        
        {/* Header */}
        <header 
          className="bg-white border-b border-gray-200 sticky top-0 z-10"
        >
          <div 
            className="mx-auto py-4"
            style={{ 
              paddingLeft: isLargeScreen ? '3rem' : '1.5rem',
              paddingRight: isLargeScreen ? '3rem' : '1.5rem'
            }}
          >
            <h1 
              className="mb-4"
              style={{ 
                fontSize: isLargeScreen ? '40px' : '28px',
                transition: 'font-size 0.3s ease'
              }}
            >
              All Recipes
            </h1>          
          {successMessage && (
            <div
              className="mb-3 rounded-xl border border-[#BFDCCB] bg-[#EAF6EE] px-3 py-2 text-sm text-[#1F5D38]"
              role="status"
              aria-live="polite"
            >
              <div className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#1F5D38]" />
                <span>{successMessage}</span>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-3 rounded-md bg-yellow-50 text-yellow-800 px-3 py-2 text-sm">
              Couldn’t load recipes from server.
              <div className="mt-1 text-xs opacity-80">Error: {String(error)}</div>
            </div>
          )}
          
          {/* Search and Filter */}
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search recipe or ingredient"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-input rounded-full border-0 focus:ring-2 focus:ring-primary outline-none"
              />
            </div>
            <button
              onClick={() => setIsFilterOpen(true)}
              className="relative w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors"
            >
              <SlidersHorizontal className="w-5 h-5 text-muted-foreground" />
              {activeFilterCount > 0 && (
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-primary-foreground rounded-full text-xs flex items-center justify-center">
                  {activeFilterCount}
                </div>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Recipe List */}
        <div 
          className="mx-auto py-6"
          style={{ 
            paddingLeft: isLargeScreen ? '3rem' : '1.5rem',
            paddingRight: isLargeScreen ? '3rem' : '1.5rem',
            display: isLargeScreen ? 'grid' : 'flex',
            flexDirection: isLargeScreen ? 'unset' : 'column',
            gridTemplateColumns: isLargeScreen ? 'repeat(auto-fill, minmax(280px, 1fr))' : 'unset',
            gap: isLargeScreen ? '1.5rem' : '1rem'
          }}
        >        
        {isLoading ? (
          <div className="text-center py-16">
            <p className="text-muted-foreground mb-2">Please wait, recipes loading...</p>
          </div>
        ) : filteredRecipes.length > 0 ? (
          filteredRecipes.map(recipe => (
            <RecipeCard
              key={recipe.id || recipe._id}
              recipe={recipe}
              onClick={() => {
                hasVisitedDetailRef.current = true;
                listScrollYRef.current = window.scrollY || window.pageYOffset || 0;
                setSelectedRecipe(recipe);
              }}
            />
          ))
        ) : recipes.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-muted-foreground mb-2">No recipes yet</p>
            <p className="text-muted-foreground text-sm mb-6">
              Add your first recipe to get started
            </p>
            <button
              onClick={handleAddRecipe}
              className="py-3 px-6 bg-primary text-primary-foreground rounded-full shadow-md hover:bg-primary-hover transition-colors inline-flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              <span>Add Recipe</span>
            </button>
          </div>
        ) : (
          <div className="text-center py-16">
            <p className="text-muted-foreground mb-2">No recipes found</p>
            <p className="text-muted-foreground text-sm">
              Try adjusting your search or filters
            </p>
          </div>
        )}
      </div>

      {/* Add Recipe FAB */}
      <button
        onClick={handleAddRecipe}
        className="fixed bottom-24 right-6 w-14 h-14 bg-primary text-primary-foreground rounded-full shadow-lg flex items-center justify-center hover:bg-primary-hover transition-all hover:scale-105 z-10"
        aria-label="Add recipe"
      >
        <Plus className="w-6 h-6" />
      </button>

      {/* Filter Modal */}
      <FilterModal
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApply={handleApplyFilters}
        availableTags={otherFilters}
        timingTags={timingFilters}
        selectedMealTypes={selectedMealTypes}
        selectedTags={selectedTags}
      />

      {/* Import Recipe Modal */}
      <ImportRecipeModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onRecipeSaved={handleRecipeSaved}
        userId={userId}
      />

      {/* Edit/Delete drawers — only when self-managing (standalone route). When embedded as
          MealPlan's tab, the parent owns these. */}
      {usesSelfActions && (
        <>
          <ManualRecipeForm
            isOpen={!!recipeActions.editingRecipe}
            onClose={() => recipeActions.setEditingRecipe(null)}
            onSave={recipeActions.handleSaveEditedRecipe}
            existingRecipe={recipeActions.editingRecipe}
            userId={userId}
          />
          <ConfirmationDrawer
            isOpen={!!recipeActions.deletingRecipeId}
            onCancel={() => recipeActions.setDeletingRecipeId(null)}
            onConfirm={recipeActions.handleConfirmDeleteRecipe}
            title="Delete Recipe?"
            message="This recipe will be permanently deleted from your collection. This action cannot be undone."
          />
        </>
      )}

      {/* Back to top helper */}
      <div
        className="px-6 pb-4 flex justify-center"
        style={{ paddingLeft: isLargeScreen ? '3rem' : '1.5rem', paddingRight: isLargeScreen ? '3rem' : '1.5rem' }}
      >
        <button
          type="button"
          onClick={() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          className="w-full md:w-auto px-4 py-2 bg-primary text-primary-foreground rounded-full hover:bg-primary-hover transition-colors text-sm inline-flex items-center justify-center gap-2 shadow-sm"
        >
          <ArrowUp className="w-4 h-4" />
          <span>Back to top</span>
        </button>
      </div>

      {/* Bottom Navigation */}
      <BottomNav activeTab={activeTab} onTabChange={handleTabChange} />
    </div>
    </div>
  );
}

export default Recipes;
